/**
 * Client-side JavaScript for the API surfaces.
 *
 * Three things the report captured and never showed: the run's own API
 * breakdowns (eight of the nine stats reached only the terminal), the
 * assertions (captured, uploaded, documented — and rendered nowhere), and the
 * per-test evidence a grid row needs to be worth scanning.
 *
 * ES5-compatible vanilla JS, concatenated into the report's single inline
 * script under a CSP that permits no external resources.
 */

export const JS_API = `
  var API_METHOD_COLOR={GET:'#60a5fa',POST:'#34d399',PUT:'#fbbf24',PATCH:'#a78bfa',DELETE:'#f87171',HEAD:'#6b7280',OPTIONS:'#6b7280'};
  var API_STATUS_ORDER=['2xx','3xx','4xx','5xx','error'];
  var API_STATUS_COLOR={'2xx':'#22c55e','3xx':'#60a5fa','4xx':'#fb923c','5xx':'#ef4444','error':'#ef4444'};

  function apiMethodColor(m){return API_METHOD_COLOR[String(m||'').toUpperCase()]||'#8b949e';}

  /* A value from an assertion. These are arbitrary JSON, so keep them short
     enough to sit on one line and let the reader open the call for the rest. */
  function fmtAssertValue(v){
    if(v===undefined)return 'undefined';
    if(v===null)return 'null';
    var s;
    if(typeof v==='string'){s=v;}
    else{try{s=JSON.stringify(v);}catch(e){s=String(v);}}
    if(s===undefined||s===null)s=String(v);
    if(s.length>120)s=s.slice(0,120)+'\\u2026';
    return s;
  }

  function statusMixTotal(mix){
    if(!mix)return 0;
    var n=0;
    for(var i=0;i<API_STATUS_ORDER.length;i++)n+=(mix[API_STATUS_ORDER[i]]||0);
    return n;
  }

  /* ── Run-level API band ── */

  function renderApiMethodCard(byMethod,total){
    var keys=[];
    for(var k in byMethod){if(byMethod.hasOwnProperty(k)&&byMethod[k]>0)keys.push(k);}
    if(keys.length===0)return '';
    keys.sort(function(a,b){return byMethod[b]-byMethod[a];});
    var max=byMethod[keys[0]]||1;
    var h='<div class="tcard"><div class="tcard-h">By method<span class="tcard-sub">'+total.toLocaleString()+' calls</span></div><div style="display:flex;flex-direction:column;gap:8px">';
    for(var i=0;i<keys.length;i++){
      var m=keys[i],c=apiMethodColor(m),pct=Math.max(2,Math.round((byMethod[m]/max)*100));
      h+='<div class="mrow"><span class="mrow-k" style="color:'+c+'">'+esc(m)+'</span>';
      h+='<span class="mrow-track"><span class="mrow-fill" style="width:'+pct+'%;background:'+c+'"></span></span>';
      h+='<span class="mrow-n">'+byMethod[m].toLocaleString()+'</span></div>';
    }
    return h+'</div></div>';
  }

  function renderApiStatusCard(mix){
    var total=statusMixTotal(mix);
    if(total===0)return '';
    var h='<div class="tcard"><div class="tcard-h">By status</div><div class="sbar">';
    for(var i=0;i<API_STATUS_ORDER.length;i++){
      var k=API_STATUS_ORDER[i],n=mix[k]||0;
      if(n===0)continue;
      h+='<span style="flex:'+n+';background:'+API_STATUS_COLOR[k]+'"></span>';
    }
    h+='</div><div class="sgrid">';
    for(var j=0;j<API_STATUS_ORDER.length;j++){
      var kk=API_STATUS_ORDER[j],nn=mix[kk]||0;
      h+='<div class="scell"><span class="spill sp-'+kk+'">'+esc(kk)+'</span>';
      h+='<span class="scell-n'+(nn===0?' is-zero':'')+'">'+nn.toLocaleString()+'</span></div>';
    }
    return h+'</div></div>';
  }

  function renderAssertionsCard(s){
    var total=vNum(s.totalAssertions);
    if(total===0)return '';
    var pass=vNum(s.passedAssertions),fail=vNum(s.failedAssertions);
    var h='<div class="tcard"><div class="tcard-h">Assertions</div>';
    h+='<div class="tbig"><span class="tbig-n">'+total.toLocaleString()+'</span><span class="tbig-l">total</span></div>';
    h+='<div class="tsplit">';
    h+='<span class="tsplit-i tsplit-ok"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>'+pass.toLocaleString()+'</span>';
    h+='<span class="tsplit-i '+(fail>0?'tsplit-bad':'')+'" style="'+(fail>0?'':'color:var(--fg-2)')+'"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>'+fail.toLocaleString()+'</span>';
    h+='</div></div>';
    return h;
  }

  function renderApiTimingCard(rt,uniqueUrls){
    if(!rt)return '';
    var h='<div class="tcard"><div class="tcard-h">Response time</div><div class="tpair">';
    h+='<div class="tpair-i"><span class="tpair-n">'+Math.round(rt.avg)+'<small>ms</small></span><span class="tpair-l">average</span></div>';
    h+='<div class="tpair-i"><span class="tpair-n">'+Math.round(rt.p95)+'<small>ms</small></span><span class="tpair-l">p95</span></div>';
    h+='<div class="tpair-i"><span class="tpair-n">'+Math.round(rt.max)+'<small>ms</small></span><span class="tpair-l">slowest</span></div>';
    if(uniqueUrls>0)h+='<div class="tpair-i"><span class="tpair-n">'+uniqueUrls.toLocaleString()+'</span><span class="tpair-l">endpoints</span></div>';
    return h+'</div></div>';
  }

  /* The band only exists when the run actually produced the evidence it
     describes — a pure-UI run renders nothing here rather than four zeroes. */
  function renderTypeBand(){
    var el=document.getElementById('type-band');
    if(!el)return;
    var s=data.summary||{};
    var cards=[];

    if(vNum(s.totalApiCalls)>0){
      var mc=renderApiMethodCard(s.apiCallsByMethod||{},vNum(s.totalApiCalls));
      if(mc)cards.push(mc);
      var sc=renderApiStatusCard(s.apiCallsByStatusRange||{});
      if(sc)cards.push(sc);
      var tc=renderApiTimingCard(s.apiResponseTime,vNum(s.uniqueApiUrls));
      if(tc)cards.push(tc);
    }
    var ac=renderAssertionsCard(s);
    if(ac)cards.push(ac);

    if(typeof renderMobileCards==='function'){
      var mob=renderMobileCards(s);
      for(var mi=0;mi<mob.length;mi++)cards.push(mob[mi]);
    }

    if(cards.length===0){el.innerHTML='';return;}
    var cls=cards.length>=3?'tband-3':'tband-2';
    /* Four cards wrap to two rows of two rather than shrinking to slivers. */
    if(cards.length===4)cls='tband-2';
    el.innerHTML='<div class="tband '+cls+'">'+cards.join('')+'</div>';
  }

  /* ── Per-row evidence ── */

  /* Status chips plus a single pass/fail mark. A row has roughly 110px for
     this, so it shows the shape of the calls, not the calls themselves. */
  function apiRowEvidence(t){
    if(!t)return '';
    var mix=t.apiStatusMix,h='';
    if(mix&&statusMixTotal(mix)>0){
      for(var i=0;i<API_STATUS_ORDER.length;i++){
        var k=API_STATUS_ORDER[i],n=mix[k]||0;
        if(n===0)continue;
        h+='<span class="achip sp-'+k+'" title="'+n+' '+esc(k)+' response'+(n===1?'':'s')+'">'+n+'</span>';
      }
    }else if(t.apiCallCount>0){
      h+='<span class="achip" style="background:rgba(255,255,255,.04);color:var(--fg-1)">'+t.apiCallCount+'</span>';
    }
    var pass=t.assertionsPassed||0,fail=t.assertionsFailed||0;
    if(pass>0||fail>0){
      if(fail>0){
        h+='<span class="amark amark-bad" title="'+fail+' assertion'+(fail===1?'':'s')+' failed"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg></span>';
      }else{
        h+='<span class="amark amark-ok" title="'+pass+' assertion'+(pass===1?'':'s')+' passed"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg></span>';
      }
    }
    return h?'<span class="arow">'+h+'</span>':'';
  }

  /* ── Detail: the assertions section ── */

  function findApiCall(apiCalls,callId){
    if(!apiCalls||!callId)return null;
    for(var i=0;i<apiCalls.length;i++){
      var c=apiCalls[i];
      if(c.id===callId||c.callId===callId)return c;
    }
    return null;
  }

  function assertionExpression(a){
    if(a.expression)return a.expression;
    /* No expression was captured; say what the assertion checked instead of
       leaving the row blank. */
    return 'expect('+String(a.type||'value')+') \\u2192 '+fmtAssertValue(a.expected);
  }

  function renderAssertionsSection(assertions,apiCalls){
    if(!assertions||assertions.length===0)return '';
    var pass=0,fail=0,i;
    for(i=0;i<assertions.length;i++){
      if(assertions[i].status==='failed')fail++;else pass++;
    }

    var h='<div class="apisec"><div class="apisec-head">';
    h+='<span class="apisec-title">Assertions</span>';
    if(pass>0)h+='<span class="apisec-badge ab-ok">'+pass+' passed</span>';
    if(fail>0)h+='<span class="apisec-badge ab-bad">'+fail+' failed</span>';
    h+='</div><div class="asec">';

    /* Failures first — the reason anyone opened this page. */
    var ordered=assertions.slice().sort(function(a,b){
      return (a.status==='failed'?0:1)-(b.status==='failed'?0:1);
    });

    for(i=0;i<ordered.length;i++){
      var a=ordered[i],failed=a.status==='failed';
      h+='<div class="arow-item'+(failed?' is-failed':'')+'">';
      h+=failed
        ? '<svg style="flex-shrink:0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>'
        : '<svg style="flex-shrink:0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>';
      h+='<span class="atype">'+esc(String(a.type||'custom'))+'</span>';
      h+='<span class="aexpr">'+esc(assertionExpression(a))+'</span>';
      if(a.location&&a.location.line)h+='<span class="aloc">:'+a.location.line+'</span>';
      h+='</div>';

      if(failed){
        h+='<div class="adiff">';
        h+='<span class="adiff-i"><span class="adiff-k">expected</span><span class="adiff-v adiff-exp">'+esc(fmtAssertValue(a.expected))+'</span></span>';
        h+='<span class="adiff-i"><span class="adiff-k">received</span><span class="adiff-v adiff-act">'+esc(fmtAssertValue(a.actual))+'</span></span>';
        h+='</div>';
        var call=findApiCall(apiCalls,a.callId);
        if(call){
          var code=call.responseStatusCode!=null?call.responseStatusCode:(call.response&&call.response.status);
          h+='<div class="acall">on '+esc(String(call.method||''))+' '+esc(urlPath(String(call.url||'')))+(code?' \\u2192 '+code:'')+'</div>';
        }
      }
    }
    return h+'</div></div>';
  }

  /* The same sentence, built from the flattened summary the index carries —
     so the run overview reads identically in embedded and streaming mode. */
  function assertionSentence(fa){
    if(!fa)return '';
    return 'A <code>'+esc(String(fa.type||'custom'))+'</code> assertion expected <code>'+
      esc(String(fa.expected))+'</code> but received <code>'+esc(String(fa.actual))+'</code>.';
  }

  /* The headline for an API failure: the assertion, not the matcher wrapper. */
  function apiFailureSentence(assertions){
    if(!assertions||assertions.length===0)return '';
    for(var i=0;i<assertions.length;i++){
      var a=assertions[i];
      if(a.status!=='failed')continue;
      return 'A <code>'+esc(String(a.type||'custom'))+'</code> assertion expected <code>'+
        esc(fmtAssertValue(a.expected))+'</code> but received <code>'+
        esc(fmtAssertValue(a.actual))+'</code>.';
    }
    return '';
  }
`;
